import { supabase } from '../lib/supabase';
import { logger } from './loggingService';

interface UserApiKey {
  id: string;
  user_id: string;
  provider: string;
  encrypted_key: string;
  nickname: string;
  is_valid: boolean;
  last_validated_at: string | null;
  last_used_at: string | null;
}

export class ApiKeyService {
  /**
   * Encrypt API key using server-side Edge Function
   * This ensures the encryption secret never leaves the server
   */
  static async encryptApiKey(
    apiKey: string,
    provider: 'openai' | 'anthropic'
  ): Promise<{ encryptedKey: string; isValid: boolean }> {
    try {
      const { data, error } = await supabase.functions.invoke('encrypt-api-key', {
        body: { apiKey, provider },
      });

      if (error) {
        logger.error('API key encryption failed', { error });
        throw new Error(`Encryption failed: ${error.message}`);
      }

      if (!data || !data.encryptedKey) {
        throw new Error('Invalid response from encryption service');
      }

      return {
        encryptedKey: data.encryptedKey,
        isValid: data.isValid || false,
      };
    } catch (error) {
      logger.error('Error calling encryption service', { error });
      throw error;
    }
  }

  /**
   * Decrypt API key using server-side Edge Function
   * This ensures the encryption secret never leaves the server
   */
  static async decryptApiKey(encryptedKey: string, keyId: string): Promise<string> {
    try {
      const { data, error } = await supabase.functions.invoke('decrypt-api-key', {
        body: { encryptedKey, keyId },
      });

      if (error) {
        logger.error('API key decryption failed', { error });
        throw new Error(`Decryption failed: ${error.message}`);
      }

      if (!data || !data.apiKey) {
        throw new Error('Invalid response from decryption service');
      }

      return data.apiKey;
    } catch (error) {
      logger.error('Error calling decryption service', { error });
      throw error;
    }
  }

  static async storeUserApiKey(
    userId: string,
    provider: 'openai' | 'anthropic',
    apiKey: string,
    nickname: string
  ): Promise<{ success: boolean; keyId?: string; error?: string }> {
    try {
      // Encrypt using server-side Edge Function (validates key automatically)
      const { encryptedKey, isValid } = await this.encryptApiKey(apiKey, provider);

      if (!isValid) {
        logger.warn('Invalid API key provided', { provider });
        return { success: false, error: `Invalid ${provider} API key` };
      }

      const { data, error } = await supabase
        .from('user_api_keys')
        .insert({
          user_id: userId,
          provider,
          encrypted_key: encryptedKey,
          nickname,
          is_valid: isValid,
          last_validated_at: new Date().toISOString(),
        })
        .select('id')
        .single();

      if (error) {
        logger.error('Error storing user API key', { error });
        return { success: false, error: error.message };
      }

      logger.info('User API key stored successfully', { keyId: data.id, provider });
      return { success: true, keyId: data.id };
    } catch (error) {
      logger.error('Error in storeUserApiKey', { error });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to store API key',
      };
    }
  }

  static async getUserApiKey(keyId: string): Promise<UserApiKey | null> {
    try {
      const { data, error } = await supabase
        .from('user_api_keys')
        .select('*')
        .eq('id', keyId)
        .eq('is_valid', true)
        .maybeSingle();

      if (error) {
        logger.error('Error fetching user API key', { error, keyId });
        return null;
      }

      return data;
    } catch (error) {
      logger.error('Error in getUserApiKey', { error, keyId });
      return null;
    }
  }

  static async getDecryptedApiKey(keyId: string): Promise<string | null> {
    try {
      const apiKey = await this.getUserApiKey(keyId);
      if (!apiKey) {
        logger.warn('API key not found', { keyId });
        return null;
      }

      // Decrypt using server-side Edge Function
      const decryptedKey = await this.decryptApiKey(apiKey.encrypted_key, keyId);

      // Update last used timestamp
      await supabase
        .from('user_api_keys')
        .update({ last_used_at: new Date().toISOString() })
        .eq('id', keyId);

      return decryptedKey;
    } catch (error) {
      logger.error('Error decrypting API key', { error, keyId });
      return null;
    }
  }

  static async listUserApiKeys(userId: string): Promise<UserApiKey[]> {
    try {
      const { data, error } = await supabase
        .from('user_api_keys')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Error listing user API keys', { error, userId });
        return [];
      }

      return data || [];
    } catch (error) {
      logger.error('Error in listUserApiKeys', { error, userId });
      return [];
    }
  }

  static async deleteUserApiKey(keyId: string, userId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('user_api_keys')
        .delete()
        .eq('id', keyId)
        .eq('user_id', userId);

      if (error) {
        logger.error('Error deleting user API key', { error, keyId, userId });
        return false;
      }

      logger.info('User API key deleted successfully', { keyId });
      return true;
    } catch (error) {
      logger.error('Error in deleteUserApiKey', { error, keyId, userId });
      return false;
    }
  }

  static async revalidateApiKey(keyId: string): Promise<boolean> {
    try {
      const apiKey = await this.getUserApiKey(keyId);
      if (!apiKey) {
        logger.warn('API key not found for revalidation', { keyId });
        return false;
      }

      // Decrypt using server-side function
      const decryptedKey = await this.decryptApiKey(apiKey.encrypted_key, keyId);
      
      // Re-encrypt to validate (the encrypt function validates automatically)
      const { isValid } = await this.encryptApiKey(
        decryptedKey,
        apiKey.provider as 'openai' | 'anthropic'
      );

      // Update validation status
      await supabase
        .from('user_api_keys')
        .update({
          is_valid: isValid,
          last_validated_at: new Date().toISOString(),
        })
        .eq('id', keyId);

      logger.info('API key revalidated', { keyId, isValid });
      return isValid;
    } catch (error) {
      logger.error('Error revalidating API key', { error, keyId });
      return false;
    }
  }
}
